import { css, StyleSheet } from 'aphrodite';
import Link from 'next/link';

import SitemapHTML from '~/components/SitemapHTML';
import Head from '~/components/Meta/Head';

import { COLORS } from '~/config/themes';
import { fetchCityHtmlSitemap } from '~/utils/Fetch/Location';
import { capitalize } from '~/utils/TextProcessing';
import { provinces, states } from '~/utils/Provinces';
import ContentWrapper from '~/components/ContentWrapper';
import Breadcrumb from '~/components/Breadcrumb';
import { notifyBugSnag } from '../../utils/ErrorChecking';
import styles from '../agents-directory/index.module.scss';

const Index = (props) => {
  const { pageData } = props;
  const { locations, state, longName } = pageData;

  const breadcrumb = {
    crumbs: ['RealEstateAgents.com', longName],
    asPath: ['/', `/${state}`],
    pathname: ['/', '/[state_slug]'],
  };

  const renderLocations = () => {
    if (locations && locations.length > 0) {
      return locations.map((location, index) => (
        <span className={styles.directory__link} key={index}>
          <Link prefetch={false} href={location.url_path}>
            {capitalize(location.city)}
          </Link>
        </span>
      ));
    }
    return null;
  };

  return (
    <div className={styles.directory}>
      <Head
        url={`${process.env.CLIENT_ORIGIN}/directory/${state}`}
        title={`Top Real Estate Agents in ${longName} by City`}
        description={`A directory of all cities in ${longName} to help you find the best real estate for your home buying or selling transaction; find the best real estate agent.`}
      />
      <ContentWrapper>
        <Breadcrumb
          passedAsPath={breadcrumb && breadcrumb.asPath}
          passedPathname={breadcrumb && breadcrumb.pathname}
          passedCrumbs={breadcrumb && breadcrumb.crumbs}
          breadcrumbReady
          noCheck
        />
        <SitemapHTML
          title={`Cities in ${longName}`}
          description={`Find the perfect real estate agent in any ${longName} city.`}
          renderItems={renderLocations}
        />
      </ContentWrapper>
    </div>
  );
};

export async function getStaticPaths() {
  return { paths: [], fallback: 'blocking' };
}

export async function getStaticProps(ctx) {
  try {
    const state = decodeURI(ctx.params.state_slug);
    const stateName = states.find(
      (s) => s.short.toLowerCase() === state.toLowerCase(),
    )?.name;
    const provinceName = provinces.CA.find(
      (p) => p.short.toLowerCase() === state.toLowerCase(),
    )?.name;

    const blocked = ['pr', 'gu', 'vi', 'as', 'mp'];
    const isBlocked = blocked.some((code) => state.includes(code));

    if ((!stateName && !provinceName) || isBlocked) {
      return {
        notFound: true,
      };
    }

    const locations = await fetchCityHtmlSitemap({ state });

    const pageData = {
      locations,
      state,
      longName: provinceName ?? stateName,
    };
    return {
      props: { pageData },
      revalidate: 60 * 5,
    };
  } catch (err) {
    console.log(err);
    const bugSnagMetadata = [
      {
        label: 'route',
        fields: {
          asPath: ctx.asPath,
          pathname: ctx.pathname,
          server_context: ctx,
          server: !!ctx.req,
        },
      },
      {
        label: 'ctx error',
        fields: {
          ctx_error: ctx.error,
        },
      },
    ];
    notifyBugSnag({ error: err, metadata: bugSnagMetadata });
    return { props: { pageData: {}, error: { code: 500 } }, revalidate: 1 };
  }
}

export default Index;
